import type { Map as MapboxMap, MapboxGeoJSONFeature } from 'mapbox-gl';

// Only the methods the box query needs, so callers can pass the raw
// react-map-gl map without a getMap() return-type mismatch.
type HitTestMap = Pick<
  MapboxMap,
  'getLayer' | 'getLayoutProperty' | 'getZoom' | 'queryRenderedFeatures'
>;

/**
 * Whether a layer is drawn now. One sharing a source (groupByTileUrl) can start
 * higher, and tiles kept from a higher zoom while new ones load still hold it.
 * @param {HitTestMap} map The Mapbox map.
 * @param {string} id The layer id.
 * @return {boolean} True while it is on the map, shown and in its zoom range.
 */
const isLayerDrawn = (map: HitTestMap, id: string): boolean => {
  const layer = map.getLayer(id);
  if (!layer || map.getLayoutProperty(id, 'visibility') === 'none') {
    return false;
  }
  // A custom layer has no zoom range.
  if (layer.type === 'custom') return true;
  const zoom = map.getZoom();
  if (layer.minzoom && zoom < layer.minzoom) return false;
  if (layer.maxzoom && zoom >= layer.maxzoom) return false;
  return true;
};

// Query the layers rendered under a small box around a screen point. Dots and
// centroids are only a few pixels wide, so an exact-point hit-test would almost
// never land on one. Shared by the well-click and document-hover popups.
export const queryFeaturesNear = (
  map: HitTestMap,
  point: { x: number; y: number },
  layerIds: string[],
  pad = 4
): MapboxGeoJSONFeature[] => {
  const layers = layerIds.filter((id) => isLayerDrawn(map, id));
  if (!layers.length) return [];
  const box: [[number, number], [number, number]] = [
    [point.x - pad, point.y - pad],
    [point.x + pad, point.y + pad],
  ];
  return map.queryRenderedFeatures(box, { layers });
};
